import React from 'react';
import { AssetClass } from '../types/assetClass';
import { Allocation } from '../types/simulation';
import { CorrelationMatrix } from '../types/correlationMatrix';
import logo from '../assets/logos/vsq-logo-blackJPG.jpg';
import PortfolioSimulationChart from './PortfolioSimulationChart';
import RiskReturnHeatmap from './RiskReturnHeatmap';

interface Props {
  scenario: {
    name: string;
    allocation: Allocation;
    expectedReturn: number;
    volatility: number;
    sharpeRatio: number;
  };
  assetClasses: AssetClass[];
  correlationMatrix: CorrelationMatrix;
  initialInvestment: number;
  monthlyContribution?: number;
  targetAmount?: number;
  years: number;
  riskTolerance: number;
  username?: string;
  onClose: () => void;
}

const ScenarioReport: React.FC<Props> = ({
  scenario,
  assetClasses,
  correlationMatrix,
  initialInvestment,
  years,
  riskTolerance,
  monthlyContribution = 0,
  targetAmount = 0,
  username,
  onClose
}) => {
  const handlePrint = () => {
    window.print();
  };

  const handleEmail = () => {
    const subject = encodeURIComponent('Your VersaQuant Portfolio Analysis Report');
    const body = encodeURIComponent(`
      Hello ${username || 'Investor'},

      Your portfolio analysis report is ready for review. Here are the key findings:

      Portfolio Strategy: ${scenario.name}
      Initial Investment: ${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(initialInvestment)}
      Monthly Contribution: ${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(monthlyContribution || 0)}
      Expected Return: ${scenario.expectedReturn.toFixed(1)}%
      Portfolio Volatility: ${scenario.volatility.toFixed(1)}%
      Sharpe Ratio: ${scenario.sharpeRatio.toFixed(2)}

      Please log in to your VersaQuant account to view the full report.

      Best regards,
      VersaQuant Team
    `);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="scenario-report" style={{
      backgroundColor: 'white',
      padding: '40px',
      maxWidth: '1000px',
      margin: '0 auto',
      position: 'relative'
    }}>
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          padding: '8px',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '24px',
          color: '#666'
        }}
      >
        ×
      </button>

      {/* Action buttons */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '60px',
        display: 'flex',
        gap: '10px'
      }}>
        <button
          onClick={handleEmail}
          style={{
            padding: '8px 16px',
            backgroundColor: '#4caf50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
        >
          <span>✉</span>
          <span>Email Report</span>
        </button>
        <button
          onClick={handlePrint}
          style={{
            padding: '8px 16px',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Print Report
        </button>
      </div>

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '40px',
        borderBottom: '2px solid #eee',
        paddingBottom: '20px'
      }}>
        <img 
          src={logo} 
          alt="VersaQuant Logo" 
          style={{ 
            height: '50px',
            marginRight: '20px'
          }}
        />
        <div>
          <h1 style={{ margin: 0, color: '#1976d2' }}>Portfolio Analysis Report</h1>
          <p style={{ margin: '5px 0 0 0', color: '#666' }}>
            Prepared for {username || 'Investor'} • Generated on {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Executive Summary */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Executive Summary</h2>
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '20px', 
          borderRadius: '8px',
          marginBottom: '20px' 
        }}>
          <p>
            This report provides a comprehensive analysis of the {scenario.name} portfolio strategy. 
            Starting with an initial investment of {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(initialInvestment)}
            {monthlyContribution > 0 ? ` and monthly contributions of ${new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(monthlyContribution)}` : ''}, 
            this portfolio is designed for a {years}-year investment horizon with a risk tolerance 
            level of {riskTolerance}/10
            {targetAmount > 0 ? `, targeting a goal of ${new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(targetAmount)}` : ''}.
          </p>

          {targetAmount > 0 && (
            <div style={{
              marginTop: '20px',
              padding: '15px',
              backgroundColor: 'white',
              borderRadius: '6px',
              border: '1px solid #ddd'
            }}>
              <h3 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Goal Analysis</h3>
              {(() => {
                const finalValue = initialInvestment * Math.pow(1 + scenario.expectedReturn / 100, years) +
                  (monthlyContribution * 12 * ((Math.pow(1 + scenario.expectedReturn / 100, years) - 1) / (scenario.expectedReturn / 100)));
                const onTrack = finalValue >= targetAmount;
                
                if (onTrack) {
                  const achievementYear = Math.ceil(
                    Math.log(targetAmount / initialInvestment) / Math.log(1 + scenario.expectedReturn / 100)
                  );
                  return (
                    <div style={{ color: '#4caf50' }}>
                      <strong>✓ On Track to Reach Goal</strong>
                      <p style={{ margin: '10px 0 0 0' }}>
                        Based on current projections, you're likely to reach your target of {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(targetAmount)} by year {achievementYear}.
                      </p>
                    </div>
                  );
                } else {
                  const targetRatio = targetAmount / finalValue;
                  const requiredInitial = Math.ceil(initialInvestment * targetRatio);
                  const additionalNeeded = targetAmount - finalValue;
                  const requiredMonthly = Math.ceil(monthlyContribution + (additionalNeeded / (years * 12)));
                  
                  return (
                    <div style={{ color: '#f44336' }}>
                      <strong>⚠ Action Required to Reach Goal</strong>
                      <p style={{ margin: '10px 0 0 0' }}>
                        To reach your target of {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(targetAmount)}, consider one of these options:
                      </p>
                      <ul style={{ margin: '10px 0 0 0' }}>
                        <li>Increase initial investment to {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(requiredInitial)}</li>
                        <li>Increase monthly contribution to {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(requiredMonthly)}</li>
                      </ul>
                    </div>
                  );
                }
              })()}
            </div>
          )}
        </div>
      </section>

      {/* Key Metrics */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Key Portfolio Metrics</h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          backgroundColor: '#f8f9fa',
          padding: '20px',
          borderRadius: '8px'
        }}>
          <div>
            <h3 style={{ margin: '0 0 5px 0', color: '#1976d2' }}>Expected Return</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              {scenario.expectedReturn.toFixed(1)}%
            </p>
          </div>
          <div>
            <h3 style={{ margin: '0 0 5px 0', color: '#1976d2' }}>Portfolio Volatility</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              {scenario.volatility.toFixed(1)}%
            </p>
          </div>
          <div>
            <h3 style={{ margin: '0 0 5px 0', color: '#1976d2' }}>Sharpe Ratio</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              {scenario.sharpeRatio.toFixed(2)}
            </p>
          </div>
        </div>
      </section>

      {/* Asset Allocation */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Asset Allocation</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #eee' }}>Asset Class</th>
              <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #eee' }}>Allocation</th>
              <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #eee' }}>Expected Return</th>
              <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #eee' }}>Volatility</th>
            </tr>
          </thead>
          <tbody>
            {assetClasses.map(assetClass => {
              const assetKey = assetClass.name.toLowerCase() as keyof Allocation;
              return (
                <tr key={assetClass.name}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{assetClass.name}</td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                    {scenario.allocation[assetKey]}%
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                    {assetClass.longTermReturn}%
                  </td>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                    {assetClass.volatility}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* Investment Theory */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Investment Theory and Methodology</h2>
        <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
          <h3>Modern Portfolio Theory</h3>
          <p>
            This portfolio is constructed based on Modern Portfolio Theory (MPT), developed by Harry Markowitz. 
            MPT suggests that by optimally combining assets with different risk-return characteristics, investors 
            can construct portfolios that maximize expected return for a given level of risk.
          </p>
          
          <h3>Risk Management</h3>
          <p>
            The portfolio's risk is managed through:
          </p>
          <ul>
            <li>Diversification across multiple asset classes</li>
            <li>Consideration of correlation between assets</li>
            <li>Risk-adjusted return optimization (Sharpe Ratio)</li>
          </ul>

          <h3>Asset Allocation Strategy</h3>
          <p>
            The asset allocation strategy is tailored to a {riskTolerance}/10 risk tolerance level, indicating 
            a {riskTolerance <= 3 ? 'conservative' : riskTolerance <= 6 ? 'moderate' : 'aggressive'} approach. 
            The {years}-year investment horizon allows for {years > 10 ? 'significant' : 'moderate'} exposure 
            to growth assets while maintaining appropriate risk controls.
          </p>
        </div>
      </section>

      {/* Yearly Returns Analysis */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Yearly Returns Analysis</h2>
        <p>
          The table below shows the projected portfolio value ranges for each year, including total contributions
          and investment gains. This helps visualize how your portfolio might grow over time through both
          contributions and investment returns.
        </p>
        <div style={{ marginTop: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #eee' }}>Year</th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #eee' }}>Total Contributions</th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #eee' }}>Expected Value</th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #eee' }}>Investment Gains</th>
                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #eee' }}>Annual Return</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: years + 1 }, (_, i) => {
                const totalContributions = initialInvestment + (monthlyContribution * 12 * i);
                const expectedValue = totalContributions * Math.pow(1 + scenario.expectedReturn / 100, i);
                const investmentGains = expectedValue - totalContributions;
                const annualReturn = i === 0 ? 0 : ((expectedValue / (expectedValue - monthlyContribution * 12)) - 1) * 100;
                
                return (
                  <tr key={i}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{i}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(totalContributions)}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(expectedValue)}
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      borderBottom: '1px solid #eee', 
                      textAlign: 'right',
                      color: investmentGains >= 0 ? '#4caf50' : '#f44336'
                    }}>
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      }).format(investmentGains)}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #eee', textAlign: 'right' }}>
                      {i === 0 ? '-' : `${annualReturn.toFixed(1)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Portfolio Simulation */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Portfolio Simulation Analysis</h2>
        <p>
          The simulation below shows potential portfolio values over time based on historical market behavior. 
          The shaded areas represent different confidence ranges, with the darker middle band showing more 
          likely outcomes and the outer bands showing potential extremes.
        </p>
        <div style={{ marginTop: '20px' }}>
          <PortfolioSimulationChart
            assetClasses={assetClasses}
            allocation={scenario.allocation}
            correlationMatrix={correlationMatrix}
            initialInvestment={initialInvestment}
            monthlyContribution={monthlyContribution}
            targetAmount={targetAmount}
            years={years}
            iterations={1000}
          />
        </div>
      </section>

      {/* Risk/Return Heatmap */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Risk/Return Analysis</h2>
        <p>
          The heatmap below visualizes the risk-return characteristics of different portfolio allocations. 
          Your current portfolio position is marked on the map, showing how it compares to other possible 
          allocations in terms of risk and return trade-offs.
        </p>
        <div style={{ marginTop: '20px' }}>
          <RiskReturnHeatmap
            assetClasses={assetClasses}
            currentAllocation={scenario.allocation}
          />
        </div>
      </section>

      {/* Correlation Analysis */}
      <section style={{ marginBottom: '40px' }}>
        <h2>Correlation Analysis</h2>
        <p>
          Asset correlations play a crucial role in portfolio diversification. Lower correlations between 
          assets can help reduce portfolio risk without necessarily sacrificing returns. The correlation 
          matrix below shows the relationships between different asset classes in the portfolio.
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
            <thead>
              <tr>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #eee' }}>Asset Class</th>
                {assetClasses.map(asset => (
                  <th key={asset.name} style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #eee' }}>
                    {asset.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assetClasses.map(asset1 => (
                <tr key={asset1.name}>
                  <td style={{ padding: '12px', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>
                    {asset1.name}
                  </td>
                  {assetClasses.map(asset2 => {
                    const correlation = correlationMatrix.correlations[
                      asset1.name.toLowerCase() as keyof typeof correlationMatrix.correlations
                    ]?.[asset2.name.toLowerCase() as keyof typeof correlationMatrix.correlations] ?? 0;
                    return (
                      <td 
                        key={asset2.name} 
                        style={{ 
                          padding: '12px', 
                          borderBottom: '1px solid #eee', 
                          textAlign: 'right',
                          backgroundColor: asset1.name === asset2.name ? '#f8f9fa' : 'transparent'
                        }}
                      >
                        {correlation.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ 
        marginTop: '60px', 
        paddingTop: '20px', 
        borderTop: '2px solid #eee',
        color: '#666',
        fontSize: '12px'
      }}>
        <p>
          This report was generated by VersaQuant's Portfolio Analysis Tool. The analysis and recommendations 
          provided are based on historical data and assumptions about future market conditions. Past performance 
          is not indicative of future results. Please consult with a financial advisor before making investment 
          decisions.
        </p>
        <p style={{ marginTop: '10px' }}>
          © {new Date().getFullYear()} VersaQuant. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default ScenarioReport;
